import { Component } from '@theme/component';

/**
 * Highlights the current day in the opening hours table and fills the
 * open/closed badge.
 *
 * Server-rendered markup carries no highlight: pages are cached and Liquid's
 * `now` is UTC, so a server-side highlight can stick on the wrong day near
 * midnight. Resolving it here keeps the markup cache-safe.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} rows - The seven day rows, in Monday-first order.
 * @property {HTMLElement} [badge] - Open/closed status target.
 * @property {HTMLElement[]} [todayLabels] - Per-row "Today" label slots.
 *
 * @extends {Component<Refs>}
 */

const TIME_ZONE = 'Europe/Amsterdam';
const WEEKDAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {number} 0 = Monday … 6 = Sunday, as observed in `timeZone`.
 */
function mondayFirstIndex(date, timeZone) {
  const weekday = new Intl.DateTimeFormat('en-US', { timeZone, weekday: 'long' }).format(date);
  const index = WEEKDAYS.indexOf(weekday);
  return index === -1 ? 0 : index;
}

/**
 * @param {Date} date
 * @param {string} timeZone
 * @returns {number} Minutes since local midnight in `timeZone`.
 */
function minutesInZone(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).formatToParts(date);
  const hour = Number(parts.find((part) => part.type === 'hour')?.value ?? '0');
  const minute = Number(parts.find((part) => part.type === 'minute')?.value ?? '0');
  // Some ICU versions render midnight as hour "24"; normalise it to 0.
  return (hour % 24) * 60 + minute;
}

/**
 * Decodes HTML entities in Liquid `| escape` output (e.g. `&#39;` / `&amp;#39;`).
 *
 * @param {string | undefined} value
 * @returns {string}
 */
function decodeHtml(value) {
  if (value == null || value === '') return '';
  const el = document.createElement('textarea');
  el.innerHTML = String(value);
  const once = el.value;
  if (once === value) return once;
  el.innerHTML = once;
  return el.value;
}

/**
 * Normalises one weekday slot to a display string. Liquid usually emits a
 * string; `| json` on a metafield drop can be `{ hours, value, day }`.
 *
 * @param {unknown} slot
 * @returns {string}
 */
function hoursString(slot) {
  if (slot == null) return '';
  if (typeof slot === 'string') return slot;
  if (typeof slot === 'object') {
    const value = slot.hours ?? slot.value;
    if (typeof value === 'string') return value;
  }
  return '';
}

/**
 * Parses a display string like "10:00 - 18:00" into minute offsets. Returns
 * null for anything without two times (any locale closed label, empty, etc.).
 *
 * @param {unknown} hours
 * @returns {{ openMinutes: number, closeMinutes: number } | null}
 */
function parseHoursRange(hours) {
  hours = hoursString(hours);
  if (!hours) return null;
  const match = hours.match(/(\d{1,2}):(\d{2})\s*[-–—]\s*(\d{1,2}):(\d{2})/);
  if (!match) return null;
  return {
    openMinutes: Number(match[1]) * 60 + Number(match[2]),
    closeMinutes: Number(match[3]) * 60 + Number(match[4]),
  };
}

/**
 * True when `nowMinutes` falls in [open, close). Handles ranges past midnight.
 *
 * @param {string} hours
 * @param {number} nowMinutes
 * @returns {boolean}
 */
function isOpenAt(hours, nowMinutes) {
  const range = parseHoursRange(hours);
  if (!range) return false;
  const { openMinutes, closeMinutes } = range;
  if (closeMinutes <= openMinutes) {
    // Wraps midnight, eg. 22:00 - 02:00.
    return nowMinutes >= openMinutes || nowMinutes < closeMinutes;
  }
  return nowMinutes >= openMinutes && nowMinutes < closeMinutes;
}

class ApcOpeningHours extends Component {
  requiredRefs = ['rows'];

  connectedCallback() {
    super.connectedCallback();
    this.render(new Date());
  }

  /**
   * Applies the highlight and badge for a given instant. `now` is a parameter
   * rather than read inline so the result can be checked against any date from
   * the console:
   *   document.querySelector('apc-opening-hours').render(new Date('2026-08-02T12:00:00Z'))
   *
   * @param {Date} now
   */
  render(now) {
    const rows = this.refs.rows;
    if (!rows?.length) return;

    const todayIndex = mondayFirstIndex(now, TIME_ZONE);
    const todayRow = rows.find((row) => Number(row.dataset.dayIndex) === todayIndex);

    if (this.dataset.highlightToday === 'true' && todayRow) {
      for (const row of rows) row.classList.remove('is-today');
      todayRow.classList.add('is-today');

      const label = this.dataset.todayLabel;
      if (label) {
        for (const slot of this.refs.todayLabels ?? []) slot.textContent = '';
        const slot = this.refs.todayLabels?.find((element) => todayRow.contains(element));
        if (slot) slot.textContent = label;
      }
    }

    const badge = this.refs.badge;
    if (this.dataset.showBadge === 'true' && badge && todayRow) {
      const open = isOpenAt(todayRow.dataset.hours ?? '', minutesInZone(now, TIME_ZONE));
      badge.textContent = open ? decodeHtml(this.dataset.openText) : decodeHtml(this.dataset.closedText);
      badge.classList.toggle('is-open', open);
      badge.classList.toggle('is-closed', !open);
      badge.hidden = false;
    }
  }
}

/**
 * Fills a showroom card's "open today" line. Lives here to share the weekday
 * helpers: same reason for being client-side, and the metaobject `day` values
 * are translated per locale, so Liquid cannot match them against `now`.
 */
class ApcTodayHours extends Component {
  connectedCallback() {
    super.connectedCallback();

    let hours = [];
    try {
      hours = JSON.parse(this.dataset.hours ?? '[]');
    } catch {
      // Malformed data: fall through to the closed text.
    }

    // Liquid emits the hours in stored order, so the index only means a weekday on a full week.
    if (hours.length !== 7) return;

    const today = hoursString(hours[mondayFirstIndex(new Date(), TIME_ZONE)]);
    // Any non-range string is closed. Copy comes from data-closed-text (locale `showroom_closed_today`).
    const openToday = parseHoursRange(today) != null;
    this.textContent = openToday
      ? decodeHtml(this.dataset.openTemplate).replace('__HOURS__', today)
      : decodeHtml(this.dataset.closedText);
    this.classList.toggle('is-closed', !openToday);
    this.hidden = false;
  }
}

/**
 * Fills a showroom status pill: open until HH:MM, opens at HH:MM, or closed.
 * Same reason for being client-side as the table above.
 */
class ApcShowroomStatus extends Component {
  connectedCallback() {
    super.connectedCallback();

    let hours = [];
    try {
      hours = JSON.parse(this.dataset.hours ?? '[]');
    } catch {
      // Malformed data: fall through to the closed text.
    }

    if (hours.length !== 7) return;

    const now = new Date();
    const today = hoursString(hours[mondayFirstIndex(now, TIME_ZONE)]);
    const range = parseHoursRange(today);
    const [opensAt, closesAt] = today.split(/\s*[-–—]\s*/);
    const minutes = minutesInZone(now, TIME_ZONE);

    let open = false;
    let text = decodeHtml(this.dataset.closedText);

    if (isOpenAt(today, minutes)) {
      open = true;
      text = decodeHtml(this.dataset.openTemplate).replace('__HOURS__', closesAt);
    } else if (range && minutes < range.openMinutes) {
      text = decodeHtml(this.dataset.opensTemplate).replace('__HOURS__', opensAt);
    }

    this.textContent = text;
    this.classList.toggle('is-closed', !open);
    this.hidden = false;
  }
}

if (!customElements.get('apc-showroom-status')) {
  customElements.define('apc-showroom-status', ApcShowroomStatus);
}

if (!customElements.get('apc-opening-hours')) {
  customElements.define('apc-opening-hours', ApcOpeningHours);
}

if (!customElements.get('apc-today-hours')) {
  customElements.define('apc-today-hours', ApcTodayHours);
}
